/**
 * Provider Constants
 *
 * Colors, gradients, and metadata for all supported AI providers.
 */

import type { AIProvider } from '@aicostguard-dev/shared';

/**
 * Provider display metadata
 */
export const PROVIDER_INFO: Record<AIProvider, {
  name: string;
  displayName: string;
  color: string;
  gradient: string;
  description: string;
}> = {
  openai: {
    name: 'openai',
    displayName: 'OpenAI',
    color: '#10A37F',
    gradient: 'from-emerald-500 to-teal-600',
    description: 'GPT-4, GPT-3.5, O1 models',
  },
  anthropic: {
    name: 'anthropic',
    displayName: 'Anthropic',
    color: '#D97757',
    gradient: 'from-orange-500 to-amber-600',
    description: 'Claude 3 & 4 family',
  },
  cohere: {
    name: 'cohere',
    displayName: 'Cohere',
    color: '#39DBBD',
    gradient: 'from-cyan-500 to-teal-500',
    description: 'Command models',
  },
  mistral: {
    name: 'mistral',
    displayName: 'Mistral AI',
    color: '#FF7000',
    gradient: 'from-orange-600 to-red-600',
    description: 'Mistral Large, Medium, Small',
  },
  gemini: {
    name: 'gemini',
    displayName: 'Google Gemini',
    color: '#4285F4',
    gradient: 'from-blue-600 to-indigo-700',
    description: 'Gemini Pro, Ultra, Flash',
  },
  // Tier B — OpenAI-compatible providers (detected via base_url).
  xai: {
    name: 'xai',
    displayName: 'xAI (Grok)',
    color: '#000000',
    gradient: 'from-zinc-800 to-zinc-950',
    description: 'Grok family via OpenAI-compatible API',
  },
  groq: {
    name: 'groq',
    displayName: 'Groq',
    color: '#F55036',
    gradient: 'from-orange-500 to-red-600',
    description: 'Ultra-low-latency Llama, Mixtral, and more',
  },
  together: {
    name: 'together',
    displayName: 'Together AI',
    color: '#0F6FFF',
    gradient: 'from-blue-500 to-blue-700',
    description: 'Hosted open-source models',
  },
  fireworks: {
    name: 'fireworks',
    displayName: 'Fireworks AI',
    color: '#5D2DE6',
    gradient: 'from-violet-500 to-violet-700',
    description: 'Fast hosted open-source models',
  },
  perplexity: {
    name: 'perplexity',
    displayName: 'Perplexity',
    color: '#20808D',
    gradient: 'from-teal-600 to-teal-800',
    description: 'Sonar models with web search',
  },
  deepseek: {
    name: 'deepseek',
    displayName: 'DeepSeek',
    color: '#4D6BFE',
    gradient: 'from-indigo-500 to-blue-700',
    description: 'DeepSeek V3 / R1 family',
  },
  openrouter: {
    name: 'openrouter',
    displayName: 'OpenRouter',
    color: '#6E56CF',
    gradient: 'from-purple-500 to-purple-700',
    description: 'Multi-provider gateway',
  },
  'vercel-ai-gateway': {
    name: 'vercel-ai-gateway',
    displayName: 'Vercel AI Gateway',
    color: '#000000',
    gradient: 'from-zinc-800 to-zinc-950',
    description: 'Unified routing for many providers',
  },
  local: {
    name: 'local',
    displayName: 'Local LLMs',
    color: '#6B7280',
    gradient: 'from-gray-600 to-slate-700',
    description: 'Self-hosted models',
  },
};

/**
 * Chart colors for providers (for consistency across charts)
 */
export const PROVIDER_CHART_COLORS: Record<AIProvider, string> = {
  openai: '#10A37F',
  anthropic: '#D97757',
  cohere: '#39DBBD',
  mistral: '#FF7000',
  gemini: '#4285F4',
  xai: '#000000',
  groq: '#F55036',
  together: '#0F6FFF',
  fireworks: '#5D2DE6',
  perplexity: '#20808D',
  deepseek: '#4D6BFE',
  openrouter: '#6E56CF',
  'vercel-ai-gateway': '#000000',
  local: '#6B7280',
};

/**
 * Get provider info by name
 */
export function getProviderInfo(provider: AIProvider | string) {
  return PROVIDER_INFO[provider as AIProvider];
}

/**
 * Get provider color for charts
 */
export function getProviderColor(provider: AIProvider | string): string {
  return PROVIDER_CHART_COLORS[provider as AIProvider] || '#6B7280';
}

/**
 * Detect provider from model name
 * Useful when API doesn't return provider explicitly
 */
export function detectProvider(modelName: string): AIProvider {
  const lower = modelName.toLowerCase();

  // Tier A — native SDK model families
  if (lower.includes('gpt') || lower.startsWith('o1') || lower.startsWith('o3') || lower.startsWith('o4') || lower.startsWith('gpt-5')) return 'openai';
  if (lower.includes('claude')) return 'anthropic';
  if (lower.includes('gemini')) return 'gemini';
  if (lower.includes('command')) return 'cohere';
  if (lower.includes('mistral') || lower.includes('mixtral') || lower.startsWith('ministral')) return 'mistral';

  // Tier B — OpenAI-compatible providers identified by model prefix when no
  // base_url info is available. These are best-effort; the receipt's
  // `provider` field set by the auto-instrument is the authoritative source.
  if (lower.startsWith('grok')) return 'xai';
  if (lower.startsWith('deepseek')) return 'deepseek';
  if (lower.startsWith('sonar') || lower.includes('perplexity')) return 'perplexity';

  // Local / open-weights
  if (lower.includes('llama') || lower.includes('phi') || lower.includes('gemma') || lower.includes('qwen')) return 'local';

  return 'local';
}

/**
 * Model to provider mapping (for mock data and chart coloring)
 */
export const MODEL_TO_PROVIDER: Record<string, AIProvider> = {
  // OpenAI
  'gpt-4': 'openai',
  'gpt-4-turbo': 'openai',
  'gpt-4o': 'openai',
  'gpt-4o-mini': 'openai',
  'gpt-3.5-turbo': 'openai',
  'o1-preview': 'openai',
  'o1-mini': 'openai',

  // Anthropic
  'claude-opus-4': 'anthropic',
  'claude-sonnet-4': 'anthropic',
  'claude-haiku-4': 'anthropic',
  'claude-opus-3.5': 'anthropic',
  'claude-sonnet-3.5': 'anthropic',
  'claude-haiku-3.5': 'anthropic',
  'claude-3-opus': 'anthropic',
  'claude-3-sonnet': 'anthropic',

  // Cohere
  'command': 'cohere',
  'command-light': 'cohere',
  'command-r': 'cohere',
  'command-r-plus': 'cohere',

  // Mistral
  'mistral-tiny': 'mistral',
  'mistral-small': 'mistral',
  'mistral-medium': 'mistral',
  'mistral-large': 'mistral',
  'mixtral-8x7b': 'mistral',

  // Gemini
  'gemini-pro': 'gemini',
  'gemini-ultra': 'gemini',
  'gemini-1.5-pro': 'gemini',
  'gemini-1.5-flash': 'gemini',

  // Local
  'llama-2-7b': 'local',
  'llama-2-13b': 'local',
  'llama-2-70b': 'local',
  'llama-3-8b': 'local',
  'llama-3-70b': 'local',
  'mistral-7b': 'local',
  'phi-2': 'local',
  'phi-3': 'local',
};

/**
 * Get provider for a model
 */
export function getModelProvider(modelName: string): AIProvider {
  return MODEL_TO_PROVIDER[modelName] || detectProvider(modelName);
}
